import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EmbeddedViewRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { IonicModule, IonSearchbar } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { VirtualTableComponent } from '../table/virtual-table.component';
import { IColumnDefinition } from '../types';
import { AlertService } from '@omni/services/alert/alert.service';
import {
  ContactOfflineService,
  ContactTag,
  CustomerTagStateCode,
  UserTagForContact,
} from '@omni/services/contact/contact.service';
import {
  DynamicForm,
  FormType,
} from '@omni/classes/dynamic-form/dynamic-form.class';
import { DEFAULT_CONTACT_DISPLAY_FORM } from '@omni/config/dynamic-forms/default-contact/default-contact-create';
import { DEFAULT_FORM_LANGUAGE_CODE } from '@omni/components/shared/ind-display-form/ind-display-form.constants';
import { DynamicFormsService } from '@omni/services/dynamic-forms/dynamic-forms-service';
import { Contact, ContactsModel } from '@omni/classes/contact/contact.class';
import { DeviceService } from '@omni/services/device/device.service';
import {
  NavigationService,
  PageName,
} from '@omni/services/navigation/navigation.service';
import { BaseTableComponent } from '../common/base-table.component';
import {
  defaultContactName,
  highlightNameMatch,
  searchContactsByName,
} from '@omni/utility/contact-name-search.utility';
import { DiskService } from '@omni/services/disk/disk.service';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { LocalizationService } from '@omni/services/localization/localization.service';
import { IndFilterMenuComponent } from '@omni/components/shared/ind-filter-menu/ind-filter-menu';
import { SelectedSuggestionPillDataModel } from '@omni/models/search-config-data-model';
import { UIService } from '@omni/services/ui/ui.service';
import { AdvancedCustomerSearchService } from '@omni/services/advanced-search/advanced-customer-search.service';
import { NewActivityComponent } from '@omni/components/activity/new-activity/new-activity';
import { TooltipModule } from '@amin-karimi/ng2-tooltip-directive';
import {
  ActivityService,
  ActivitySource,
} from '@omni/services/activity/activity.service';
import { TagVisibility } from '@omni/services/user-tag/user-tag.service';
import { differenceBy, sortBy, uniqBy } from 'lodash-es';
import { AutoFocusSearchbarDirective } from '@omni/directives/autofocus.directive';
import { EventName, EventsService } from '@omni/services/events/events.service';
import { takeUntil } from 'rxjs';
import { ContactDragStateService } from '@omni/services/drag-drop/contact-drag-state.service';
import { HapticsService } from '@omni/services/haptics/haptics.service';
import { ProductRatingOfflineService } from '@omni/services/product-rating/product-rating-offline.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { buildRowDragGhost } from '@omni/services/drag-drop/drag-ghost.util';

@Component({
  selector: 'contacts-table',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    VirtualTableComponent,
    TooltipModule,
    AutoFocusSearchbarDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: '../common/base-table.component.html',
  styleUrl: '../common/base-table.component.scss',
})
export class ContactsTableComponent
  extends BaseTableComponent
  implements OnDestroy
{
  @ViewChild('configureColumnsModal') configureColumnsModal: TemplateRef<any>;
  @ViewChild('configureFilterModal') configureFilterModal: TemplateRef<any>;
  @ViewChild('configureTagsModal') configureTagsModal: TemplateRef<any>;

  @Input() public searchText: string = '';
  @Output() public selectionChanged = new EventEmitter<Set<string>>();
  @Output() public filterChanged = new EventEmitter<
    SelectedSuggestionPillDataModel[]
  >();

  private _contacts: {
    fullname: string;
    primaryaddress: string;
    id: string;
    initial: string;
    __contact: Contact;
    __avatar: Promise<string>;
    [key: string]: any;
  }[] = [];
  private _selectedFilterData: SelectedSuggestionPillDataModel[] = [];
  private _selectedDateFromFilter: { startDate: Date; endDate: Date };

  protected readonly tableConfigKey = DB_KEY_PREFIXES.CONTACT_TABLE_CONFIG;
  protected readonly searchKeys = ['fullname'];

  /**
   * Contact name search via the shared utility so this pane stays consistent with
   * the contact-list and the search-suggestion popover: exact > starts-with >
   * token-prefix > contains > light fuzzy, with any-order multi-word matching
   * (incl. middle names). Names are read from the live Contact (firstName/middleName/
   * lastName) and the visible `fullname` is highlighted.
   */
  protected override searchItems(items: any[], searchText: string): any[] {
    return searchContactsByName(
      items,
      searchText,
      {},
      (row: any) => defaultContactName(row?.__contact) || row?.fullname || '',
    ).map((row: any) => ({
      ...row,
      __highlighted: { fullname: highlightNameMatch(row?.fullname || '', searchText) },
    }));
  }

  @ViewChild('tagsSearchbar', { static: false }) set tagsSearchbar(
    value: IonSearchbar,
  ) {
    if (value) {
      value.setFocus();
    }
  }

  constructor(
    readonly disk: DiskService,
    readonly alertService: AlertService,
    public readonly contactService: ContactOfflineService,
    readonly dynamicFormService: DynamicFormsService,
    readonly translate: TranslateService,
    readonly device: DeviceService,
    readonly cdr: ChangeDetectorRef,
    readonly localizationService: LocalizationService,
    private readonly navService: NavigationService,
    private readonly searchService: AdvancedCustomerSearchService,
    private readonly uiService: UIService,
    private readonly activityService: ActivityService,
    public readonly events: EventsService,
    private readonly contactDragState: ContactDragStateService,
    private readonly haptics: HapticsService,
    private readonly productRatingService: ProductRatingOfflineService,
    private readonly notificationService: NotificationService,
  ) {
    super(
      disk,
      alertService,
      dynamicFormService,
      translate,
      device,
      cdr,
      localizationService,
    );

    // Contact addresses are merged onto the live contact objects asynchronously
    // by the linked-entity Web Worker, which fires FINISHEDSERVICEWORKERMAPPING
    // on completion. On fresh login this happens AFTER the sync-completed events
    // that build this table, so the cached Address column stays blank until the
    // next sync. Re-map the rows when the worker finishes so addresses appear
    // without requiring a delta sync (OMNI-60589).
    this.events
      .observe(EventName.FINISHEDSERVICEWORKERMAPPING)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshRowsAfterWorkerMapping());
  }

  ngOnChanges(changes: SimpleChanges) {
    super.onChanges(changes);
  }

  ngOnDestroy() {
    super.onDestroy();
  }

  @HostListener('window:omni:sync:completed', ['$event'])
  @HostListener('window:omni:offlineDataLoad:Completed', ['$event'])
  syncCompleted() {
    super.onSyncCompleted();
  }

  protected getDefaultColumns(): IColumnDefinition[] {
    return [
      {
        name: 'Customer Name',
        field: 'fullname',
        width: 200,
        sticky: 'left',
        sortable: true,
        avatarField: 'avatar',
        avatarFallbackField: 'initial',
        showAlways: true,
      } as IColumnDefinition,
      {
        name: 'Address',
        field: 'primaryaddress',
        width: 200,
        sortable: true,
      } as IColumnDefinition,
    ];
  }

  getFormDefinition(): { form: DynamicForm; langCode: string } {
    let form = this.dynamicFormService.getFormDefinitionForEntity(
      'contact',
      FormType.DISPLAYFORM,
    );
    let langCode = this.localizationService.selectedLanguage.localeID;
    if (!form) {
      form = new DynamicForm(DEFAULT_CONTACT_DISPLAY_FORM['value'][0]);
      langCode = DEFAULT_FORM_LANGUAGE_CODE;
    }
    return {
      form,
      langCode,
    };
  }

  protected async onInitData() {
    // if (this.contactService.contacts.length === 0) {
    await this.contactService.fetchContactsForConfiguredDisplay(
      false,
      false,
      null,
      false,
      false,
      null,
    );
    // }
    const defaultFilters = this.searchService.initContactSearchConfig();
    this._selectedFilterData = defaultFilters;

    await this._applyFilters(
      this._selectedFilterData,
      this._selectedDateFromFilter,
    );
  }

  private async _applyFilters(
    filters: SelectedSuggestionPillDataModel[],
    dateFilter: typeof this._selectedDateFromFilter,
  ) {
    const searchResult = await this.searchService.getContactsWithFilters(
      filters,
      dateFilter,
    );
    const _this = this;
    const contactDisplaySequence = Number.parseInt(
      localStorage.getItem('contactdisplaysequence') || '0',
    );
    this._contacts = searchResult.value().map((contact) => {
      const fn = contact.firstName;
      const ln = contact.lastName;
      const fullname =
        fn && ln
          ? contactDisplaySequence === 548910001
            ? `${ln} ${fn}`
            : `${fn} ${ln}`
          : fn || ln || '';
      return {
        ...contact.raw,
        id: contact.ID,
        initial: contact.initial,
        primaryaddress: contact.getPrimaryAddress,
        fullname,
        __contact: contact,
        __avatar: undefined,
        get avatar() {
          if (!this.__avatar) {
            this.__avatar = Promise.resolve(
              _this.contactService.getEntityImageStringForContact(this.__contact),
            );
          }
          return this.__avatar;
        },
      };
    });
    this._selectedFilterData = filters;
    this._selectedDateFromFilter = dateFilter;
    this.filterChanged.emit(this._selectedFilterData);
  }

  private _isRefreshingAfterWorkerMapping = false;

  private async refreshRowsAfterWorkerMapping() {
    // Nothing to refresh until the table has built its rows at least once.
    if (!this._selectedFilterData || this._isRefreshingAfterWorkerMapping) return;
    this._isRefreshingAfterWorkerMapping = true;
    try {
      await this._applyFilters(
        this._selectedFilterData,
        this._selectedDateFromFilter,
      );
      await this.refreshData();
    } finally {
      this._isRefreshingAfterWorkerMapping = false;
    }
  }

  protected getItems(): any[] {
    return this._contacts;
  }

  async onRowAction(row: any, action: 'new-activity' | 'tags' | 'more') {
    if (action === 'new-activity') {
      const contact = row.__contact;
      this.highlightedRows = contact ? new Set([contact.ID]) : new Set();
      this.cdr.markForCheck();
      await this.createNewActivity(contact);
    }
  }

  override rowDraggable = true;

  /** Populate the HTML5 dataTransfer payload on row dragstart and swap the
   *  default drag preview (the whole row) for a card chip that matches the
   *  calendar event style. Single delegated listener in the parent
   *  virtual-table — no per-row binding, no leak under virtual-scroll recycle. */
  override onRowDragStart({ row, event }: { row: any; event: DragEvent }): void {
    const contact: Contact | undefined = row?.__contact;
    if (!contact) return;
    const fullName: string = row.fullname || contact.fullName || 'Customer';
    const payload = { contactId: contact.ID, fullName };

    // Cross-component handoff to the calendar goes through app state, not
    // dataTransfer: Android System WebView strips custom MIME types from the
    // drag, so the contact id can't survive there (see ContactDragStateService).
    this.contactDragState.beginDrag(payload);
    this.haptics.dragReady();

    if (!event.dataTransfer) return;
    // Set only text/plain (a standard type) for desktop drag semantics and a
    // sensible label on external drops. We deliberately do NOT set a custom MIME
    // type: iOS WKWebView stalls the drop ~5s resolving the custom UTI's item
    // provider, and the payload already travels via ContactDragStateService.
    event.dataTransfer.setData('text/plain', fullName);
    // 'move' (not 'copy') so the OS doesn't render a "+" cursor over the chip.
    event.dataTransfer.effectAllowed = 'move';

    const ghost = buildRowDragGhost(fullName, this.translate.instant('EMAIL_TEMPLATE_NEW_MEETING'));
    document.body.appendChild(ghost);
    event.dataTransfer.setDragImage(ghost, 16, 18);
    // The browser snapshots the element synchronously; safe to remove next tick.
    requestAnimationFrame(() => ghost.remove());
  }

  private newActivityCallbackId: string;

  private async createNewActivity(contact: Contact) {
    this.uiService.showRightPane = true;
    this.uiService.activeViewNewMeeting = 'meeting';
    this.activityService.activitySource = ActivitySource.CONTACT_INFO;
    this.contactService.contactInformation = contact;
    const callbackEvent = (callbackId: string) => (done, destroy) => {
      this.highlightedRows = new Set();
      if (callbackId !== this.newActivityCallbackId || done) return;
      if (!destroy) {
        this.uiService.showRightPane = false;
      }
      this.cdr.markForCheck();
    }
    this.newActivityCallbackId = new Date().getTime().toString();

    this.contactService.consentFrom = 'ContactTool';
    await this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView, PageName.ContactPageComponent);
    await this.navService.pushChildNavPageWithPageTracking(
      NewActivityComponent,
      PageName.NewActivityComponent,
      PageName.ContactPageComponent,
      {
        from: 'ContactPageComponent',
        contact,
        callbackEvent: callbackEvent(this.newActivityCallbackId),
      },
    );
    this.events.publish('newActivityOpenedChanged', true);
  }

  async onRowClicked(row: any) {
    const contact = row.__contact as Contact;
    this.contactService.isClickedGoToContactsTool = true;
    this.contactService.contactInformation = contact;

    this.navService.pushWithPageTracking(
      PageName.ContactPageComponent,
      PageName.ContactPageComponent,
      {
        from: 'ContactTool',
        __source: 'menu',
        contactInformation: contact,
      },
      PageName.ContactPageComponent,
    );

    // For global search contacts (not in My Customers / local DB), affiliations are not
    // on disk, so fetch them from CRM and update contactInformation to trigger a re-render.
    const isGlobalSearchContact = !this.contactService.getContactByID(contact.ID);
    if (isGlobalSearchContact) {
      const temp = await this.contactService.fetchContactsForConfiguredDisplay(true, false, contact.ID, true);
      if (temp && temp[0]) {
        const updatedContact = new Contact(temp[0]);
        this.contactService.rawContactInformation = updatedContact;
        if (this.contactService.contactInformation?.ID === updatedContact.ID) {
          this.contactService.contactInformation = updatedContact;
        }
      }
    }
  }

  protected async configureFilter() {
    this.searchService.initContactSearchConfig();
    if (this.productRatingService.syncState$.value === 'syncing') {
      // No translation key added for this yet — plain string until product/localization signs off on copy.
      this.notificationService.notify(
        'Adoption ladder, product, and segment data is still syncing — some options may be unavailable.',
        'ContactsTableComponent',
        'top',
        ToastStyle.INFO,
      );
    }
    const result = await this.alertService.showPanel({
      title: 'All Filters',
      content: {
        component: IndFilterMenuComponent,
        inputs: {
          viewData: {
            isMultipleSelectionEnabled: true,
            title: this.translate.instant('FILTERS'),
            isSearchEnabled: true,
            isReadOnly: true,
            selectedDate: this._selectedDateFromFilter,
          },
          selectedFilter: this._selectedFilterData,
          from: PageName.ContactPageComponent,
          subFrom: ContactsModel.MY_CONTACTS_TAB,
        },
      },
    });
    const data = result.data;
    if (!data?.isDone) return;
    let selectedItems = (data.selectedItems ||
      []) as SelectedSuggestionPillDataModel[];
    selectedItems.sort((a, b) => (a.createdOn < b.createdOn ? 1 : -1));
    let dateFilter: typeof this._selectedDateFromFilter;
    if (data.selectedDate?.startDate && data.selectedDate?.endDate) {
      dateFilter = {
        startDate: new Date(data.selectedDate.startDate),
        endDate: new Date(data.selectedDate.endDate),
      };
    }
    await this.uiService.displayLoader();
    await this._applyFilters(selectedItems, dateFilter);
    await this.refreshData();
    await this.uiService.dismissLoader();
    this.cdr.markForCheck();
  }

  private getTags() {
    const selectedContact = this.selectedRows.size === 1 ? Array.from(this.selectedRows)[0] : null;
    const contactTags = uniqBy(this.contactService.contactTags, 'indskr_usertagid');
    const selectedTags = selectedContact ? contactTags.filter(tag => tag.contacts.some(contact => contact.contactId === selectedContact)).map(tag => tag.indskr_usertagid) : [];
    return sortBy(
      contactTags.filter(
        (tag) =>
          tag.visibility == TagVisibility.PERSONAL || tag.allowUserToModifytag,
      ),
      'indskr_name',
    ).map((tag) => ({
      id: tag.indskr_usertagid,
      name: tag.indskr_name,
      selected: selectedTags.includes(tag.indskr_usertagid),
      deleted: undefined as boolean | undefined,
    }));
  }

  protected configureTags() {
    let tags = this.getTags();
    const savedTags = tags.filter(tag => tag.selected && !!tag.id).map(tag => tag.id);

    this.alertService.showPanel({
      title: 'All Tags',
      content: {
        template: this.configureTagsModal,
        context: {
          $implicit: tags,
          onSearch: (searchText, self) => {
            const filteredTags = tags.filter((tag) =>
              tag.name.toLowerCase().includes(searchText.toLowerCase()),
            );
            self.context.$implicit = filteredTags;
            self.context.searchText = searchText;
            self.markForCheck();
          },
        },
      },
      buttons: [
        {
          key: 'search',
          icon: 'assets/imgs/search-icon.svg',
          header: true,
          action(view: EmbeddedViewRef<any>) {
            view.context.showSearchBar = !view.context.showSearchBar;
            this.selected = view.context.showSearchBar;
            if (!view.context.showSearchBar) {
              view.context.onSearch('', view);
            }
            view.markForCheck();
          },
        },
        {
          key: 'create-tag',
          label: 'Create Tag',
          align: 'left',
          action: async (view: EmbeddedViewRef<any>) => {
            await this.createNewTagAlert();
            tags = this.getTags();
            view.context.$implicit = tags;
            view.markForCheck();
          },
        },
        {
          key: 'cancel',
          label: 'Clear Tags',
          action: (view: EmbeddedViewRef<any>) => {
            for (const tag of tags) {
              tag.selected = false;
            }
            view.markForCheck();
          },
        },
        {
          key: 'ok',
          label: 'Apply',
          primary: true,
          action: async () => {
            const selectedTags = tags.filter(tag => tag.selected);
            const tagsToRemove = savedTags.filter(tag => !selectedTags.some(t => t.id === tag));
            const tagsToAdd = selectedTags.filter(tag => !savedTags.includes(tag.id));
            const tagsToDelete = tags.filter(tag => tag.deleted).map(t => t.id);
            if (tagsToAdd.length || tagsToRemove.length || tagsToDelete.length) {
              await this.addContactsToTags(Array.from(this.selectedRows), tagsToAdd, tagsToRemove, tagsToDelete);
            }
            return true;
          },
        },
      ],
    });
  }

  private async createNewTagAlert(failedText?: string) {
    const res = await this.alertService.showAlert(
      {
        title: this.translate.instant('CREATE_TAG'),
        subTitle: failedText ?? '',
        cssClass: 'saveSearchAlert',
        inputs: [
          {
            type: 'text',
            name: 'name',
            placeholder: this.translate.instant('ENTER_TAG_NAME'),
          },
        ],
      },
      this.translate.instant('CREATE'),
      this.translate.instant('CANCEL'),
    );
    if (res.role !== 'ok') return;
    const tagName = res.data.values.name;
    if (!tagName) {
      this.createNewTagAlert(this.translate.instant('TAG_NAME_IS_REQUIRED'));
      return;
    }
    await this.addContactsToTags(Array.from(this.selectedRows), [{name: tagName}]);
  }

  private async addContactsToTags(contactIds: string[], tags: ({id: string} | {name: string})[], tagsToRemove: string[] = [], tagsToDelete: string[] = []) {
    const tagsToUpdate: UserTagForContact[] = [];
    const contactTags = contactIds.map(id => new ContactTag(id));
    for (const tag of tags) {
      let tagToUpdate: UserTagForContact;
      if ('id' in tag) {
        tagToUpdate = this.contactService.contactTags.find(t => t.indskr_usertagid === tag.id);
      } else {
        tagToUpdate = new UserTagForContact(
          null,
          null,
          false,
          tag.name,
          [],
          false,
          CustomerTagStateCode.Active,
          TagVisibility.PERSONAL,
        );
      }
      tagToUpdate.contacts.push(...differenceBy(contactTags, tagToUpdate.contacts, 'contactId'));
      tagsToUpdate.push(tagToUpdate);
    }
    if (tagsToRemove.length && contactIds.length === 1) {
      for (const tagId of tagsToRemove) {
        for (const tag of this.contactService.contactTags) {
          if (tag.indskr_usertagid === tagId) {
            for (const contact of tag.contacts) {
              if (contact.contactId === contactIds[0]) {
                contact.deleted = true;
                if (!tagsToUpdate.some(t => t.indskr_usertagid === tagId)) {
                  tagsToUpdate.push(tag);
                }
              }
            }
          }
        }
      }
    }
    if (tagsToDelete.length) {
      for (const tagId of tagsToDelete) {
        for (const tag of this.contactService.contactTags) {
          if (tag.indskr_usertagid === tagId) {
            tag.deleted = true;
            tag.contacts = [];
            if (!tagsToUpdate.some(t => t.indskr_usertagid === tagId)) {
              tagsToUpdate.push(tag);
            }
          }
        }
      }
    }
    await this.updateTags(tagsToUpdate);
    if (tagsToRemove.length && contactIds.length === 1) {
      for (const tagId of tagsToRemove) {
        for (const tag of this.contactService.contactTags) {
          if (tag.indskr_usertagid === tagId) {
            tag.contacts = tag.contacts.filter(c => !c.deleted);
          }
        }
      }
    }
  }

  private async updateTags(tags: UserTagForContact[]) {
    const newTags = [];
    for (const tag of tags) {
      const isNew = !tag.indskr_usertagid;
      if (tag.deleted) {
        await this.contactService.deleteContactTag(tag);
      } else {
        const createTagResult =
          await this.contactService.createOrUpdateContactTag(tag, true);
        if (createTagResult.length) {
          const newTag = createTagResult[0];
          tag.indskr_usertagid = newTag.indskr_usertagid;
          tag.indskr_externalid = newTag.indskr_externalid;
        }
        if (isNew) {
          newTags.push(tag);
        }
      }
    }
    this.contactService.contactTags = sortBy(
      [...this.contactService.contactTags, ...newTags],
      'indskr_name',
    );
  }
}
